import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
  FaUserCheck,
  FaUserTimes,
  FaUsers,
  FaClock,
  FaSearch,
  FaStore,
  FaFileExcel,
} from "react-icons/fa";
import { axiosMaster, axiosSalary } from "../api/axiosInstance";
import { useAuth } from "../auth/AuthContext";


const AtteLogTodayPrecentByGroup = () => {

  /* ================= SAFE USER ================= */

  const { user } = useAuth();

  const groupId = user?.groupId;
  const userTypeId = user?.userTypeId;
  const restaurantId = user?.restaurantId;

  /* ================= STATE ================= */

  const [presentData, setPresentData] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [restMap, setRestMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("total");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [restaurantOptions, setRestaurantOptions] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  /* ================= FETCH ================= */

  const fetchData = async (restId = 0) => {
    try {
      if (!groupId) return;

      setLoading(true);

      const [attRes, restRes, empRes] = await Promise.all([
        axiosSalary.get(`/AttendanceLog/GetPresentTodayByGroup?groupId=${groupId}`),
        axiosMaster.get(`/RestaurantMaster/GetByGroup?groupId=${groupId}`),
        axiosMaster.get(`/EmployeeMaster/GetByGroup?groupId=${groupId}`),
      ]);

      /* ================= PRESENT ================= */
      let presentList = attRes.data || [];

      if (userTypeId === 4 && restaurantId) {
        presentList = presentList.filter(
          (item) => item.restId === restaurantId
        );
      }

      setPresentData(presentList);

      /* ================= EMPLOYEE ================= */
      let empList = empRes.data || [];

      if (userTypeId === 4 && restaurantId) {
        empList = empList.filter(
          (e) => e.restId === restaurantId
        );
      }

      setEmployeeData(empList);
      setLastUpdated(new Date());

      /* Restaurant Map */
      const map = {};
      const options = [{ value: 0, label: "All Restaurants" }];

      (restRes.data || []).forEach((r) => {
        map[r.restId] = r.restaurantName;
        options.push({
          value: r.restId,
          label: r.restaurantName,
        });
      });

      setRestMap(map);
      setRestaurantOptions(options);

      if (!selectedRestaurant) {
        setSelectedRestaurant(options[0]);
      }

    } catch (err) {
      console.error("API error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= AUTO REFRESH ================= */

  useEffect(() => {
    if (!groupId) return;

    fetchData(selectedRestaurant?.value || 0);

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchData(selectedRestaurant?.value || 0);
      }
    }, 30000);

    return () => clearInterval(interval);

  }, [groupId, selectedRestaurant]);

  /* ================= OPTIMIZED MAP ================= */

  const empMap = useMemo(() => {
    const map = {};
    employeeData.forEach((e) => {
      map[e.empId] = e;
    });
    return map;
  }, [employeeData]);

    /* ================= PRESENT IDS ================= */

// Employee ID Set
const employeeIdSet = useMemo(() => {
  return new Set(employeeData.map((e) => e.empId));
}, [employeeData]);

// ✅ Valid + Unique Present IDs only
const presentIds = useMemo(() => {
  const set = new Set();

  presentData.forEach((p) => {
    if (employeeIdSet.has(p.empId)) {
      set.add(p.empId); // removes duplicates
    }
  });

  return set;
}, [presentData, employeeIdSet]);

  /* ================= MERGE PRESENT ================= */

  const presentWithDetails = useMemo(() => {
  return [...presentIds].map((empId) => {
    const emp = empMap[empId];
    const punch = presentData.find((p) => p.empId === empId);

    return {
      ...emp,
      empId,
      empName: emp?.empName,
      empMobileNo: emp?.empMobileNo || "-",
      restId: emp?.restId || punch?.restId,
      firstPunchDisplayTime: punch?.firstPunchDisplayTime,
    };
  });
}, [presentIds, empMap, presentData]);


  /* ================= ABSENT ================= */

  const absentData = useMemo(() => {
    return employeeData.filter((emp) => !presentIds.has(emp.empId));
  }, [employeeData, presentIds]);

  /* ================= SEARCH ================= */

  const filterFn = (list) => {
    const term = search.toLowerCase().trim();
    return list.filter((item) => {
      if (!term) return true;
      return (
        item.empName?.toLowerCase().includes(term) ||
        item.empMobileNo?.includes(term)
      );
    });
  };

  const filterByRestaurant = (list) => {
    if (userTypeId === 4 && restaurantId) {
      return list.filter((item) => item.restId === restaurantId);
    }
    if (!selectedRestaurant || selectedRestaurant.value === 0) return list;
    return list.filter((item) => item.restId === selectedRestaurant.value);
  };

  const filteredPresent = filterFn(filterByRestaurant(presentWithDetails));
  const filteredAbsent = filterFn(filterByRestaurant(absentData));
  const filteredTotal = filterFn(filterByRestaurant(employeeData));

  const handleRestaurantChange = (selected) => {
    setSelectedRestaurant(selected || restaurantOptions[0]);
    fetchData(selected?.value || 0);
  };

  /* ================= EXCEL ================= */

  const exportExcel = () => {
    let exportData = [];

    if (activeTab === "present") exportData = filteredPresent;
    else if (activeTab === "absent") exportData = filteredAbsent;
    else exportData = [...filteredPresent, ...filteredAbsent];

    if (!exportData.length) return;

    const rows = exportData.map((r) => ({
      EmpId: r.empId,
      Name: r.empName,
      Mobile: r.empMobileNo || "-",
      Restaurant: restMap[r.restId] || "",
      PunchTime: r.firstPunchDisplayTime ? `="${r.firstPunchDisplayTime}"` : "",
      Status: presentIds.has(r.empId) ? "Present" : "Absent",
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((row) =>
          Object.values(row)
            .map((val) => `"${String(val).replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "AttendanceReport.csv";
    link.click();
  };

  /* ================= UI ================= */

  return (
    <div className="bg-white rounded-xl shadow-md p-4 border">

      {/* HEADER */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 mb-4">

        {/* LEFT */}
        <div>
          <h2 className="text-lg md:text-xl font-bold text-slate-800 flex items-center gap-2">
            <FaUserCheck className="text-green-600" />
            {activeTab === "total" && "Today's Attendance"}
            {activeTab === "present" && "Present Employees"}
            {activeTab === "absent" && "Absent Employees"}
          </h2>

          <p className="text-xs text-gray-500">Live attendance tracking</p>

          {lastUpdated && (
            <p className="text-xs text-gray-400">
              Updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* RIGHT */}
        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">

          {userTypeId !== 4 && (
            <div className="w-full sm:w-48">
              <Select
                options={restaurantOptions}
                value={selectedRestaurant}
                onChange={handleRestaurantChange}
                placeholder="Restaurant..."
                isClearable
              />
            </div>
          )}

          <div className="relative w-full sm:w-48">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border rounded-lg text-sm w-full"
            />
          </div>

          <button
            onClick={exportExcel}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <FaFileExcel /> Excel
          </button>

        </div>

      </div>

      {/* CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">

        <div
          onClick={() => setActiveTab("total")}
          className={`p-3 rounded-lg text-center cursor-pointer ${activeTab === "total" ? "bg-blue-500 text-white" : "bg-blue-100"}`}
        >
          <FaUsers className="mx-auto mb-1" />
          <p>Total</p>
          <b>{filteredTotal.length}</b>
        </div>

        <div
          onClick={() => setActiveTab("present")}
          className={`p-3 rounded-lg text-center cursor-pointer ${activeTab === "present" ? "bg-green-500 text-white" : "bg-green-100"}`}
        >
          <FaUserCheck className="mx-auto mb-1" />
          <p>Present</p>
          <b>{filteredPresent.length}</b>
        </div>

        <div
          onClick={() => setActiveTab("absent")}
          className={`p-3 rounded-lg text-center cursor-pointer ${activeTab === "absent" ? "bg-red-500 text-white" : "bg-red-100"}`}
        >
          <FaUserTimes className="mx-auto mb-1" />
          <p>Absent</p>
          <b>{filteredAbsent.length}</b>
        </div>

      </div>

      {/* LOADING */}
      {loading && <p className="text-center py-6 text-gray-500">Loading...</p>}

      {/* EMPTY */}
      {!loading && filteredTotal.length === 0 && (
        <p className="text-center py-6 text-gray-500">No employees found</p>
      )}

      {/* TOTAL VIEW */}
      {activeTab === "total" && (
        <>
          {/* PRESENT SECTION */}
          <div className="mb-4">
            <h3 className="text-green-700 font-semibold mb-2 border-b pb-1">
              ✅ Present Employees ({filteredPresent.length})
            </h3>
            <div className="space-y-2">
              {filteredPresent.map((item) => (
                <div
                  key={item.empId}
                  className="border rounded-lg p-3 shadow-sm hover:shadow-md transition bg-white"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <div>
                      <p className="font-semibold text-sm md:text-base text-slate-800">
                        {item.empName}
                      </p>
                      <p className="text-xs text-gray-500">📱 {item.empMobileNo}</p>
                      <p className="text-xs text-purple-600 flex items-center gap-1">
                        <FaStore />
                        {restMap[item.restId] || "Unknown"}
                      </p>
                    </div>
                    <div className="text-blue-600 text-xs sm:text-sm flex items-center gap-1">
                      <FaClock />
                      {item.firstPunchDisplayTime}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ABSENT SECTION */}
          <div>
            <h3 className="text-red-700 font-semibold mb-2 border-b pb-1">
              ❌ Absent Employees ({filteredAbsent.length})
            </h3>
            <div className="space-y-2">
              {filteredAbsent.map((item) => (
                <div
                  key={item.empId}
                  className="border rounded-lg p-3 bg-red-50 shadow-sm"
                >
                  <p className="font-semibold text-sm md:text-base text-slate-800">
                    {item.empName}
                  </p>
                  <p className="text-xs text-gray-500">📱 {item.empMobileNo}</p>
                  <p className="text-xs text-purple-600 flex items-center gap-1">
                    <FaStore />
                    {restMap[item.restId] || "Unknown"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* PRESENT TAB */}
      {activeTab === "present" && (
        <div className="space-y-2">
          {filteredPresent.map((item) => (
            <div
              key={item.empId}
              className="border rounded-lg p-3 shadow-sm hover:shadow-md transition"
            >
              <div className="flex flex-col sm:flex-row justify-between gap-2">
                <div>
                  <p className="font-semibold text-sm md:text-base">{item.empName}</p>
                  <p className="text-xs text-gray-500">📱 {item.empMobileNo}</p>
                  <p className="text-xs text-purple-600 flex items-center gap-1">
                    <FaStore />
                    {restMap[item.restId] || "Unknown"}
                  </p>
                </div>
                <div className="text-blue-600 text-xs sm:text-sm flex items-center gap-1">
                  <FaClock />
                  {item.firstPunchDisplayTime}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ABSENT TAB */}
      {activeTab === "absent" && (
        <div className="space-y-2">
          {filteredAbsent.map((item) => (
            <div
              key={item.empId}
              className="border rounded-lg p-3 bg-red-50 shadow-sm"
            >
              <p className="font-semibold text-sm md:text-base text-slate-800">
                {item.empName}
              </p>
              <p className="text-xs text-gray-500">📱 {item.empMobileNo}</p>
              <p className="text-xs text-purple-600 flex items-center gap-1">
                <FaStore />
                {restMap[item.restId] || "Unknown"}
              </p>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

export default AtteLogTodayPrecentByGroup;